import { useState, useEffect } from 'react';

import { Alert, App, Form, Button, Divider, Input, Space, Typography, Upload, } from 'antd';

import { API_LOGOUT_ALL_URL, API_PROFILE_URL, API_RESEND_VERIFICATION_URL, API_USER_ME_DELETE_PREVIEW_URL, API_USER_ME_URL, API_USER_URL, isAdmin } from '../utils';
import { clearSessionToken } from '../utils/session';
import { apiErrorMessage } from '../utils/apiErrors';
import { AdminJSON } from '../utils/admin';
import { InputColor, InputIconSelect, InputText } from '../components/Input';
import { api } from '../api/client';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { deleteFile, uploadFile } from '../services/files';
import { clearLocalDrafts } from '../services/drafts';
import { AVATAR_MAX_MB, AVATAR_MIMES, normalizeAvatarImage } from '../utils/imageNormalize';
import UserAvatar from '../components/users/UserAvatar';
import VerifiedBadge from '../components/users/VerifiedBadge';
import { complaintSentence, deliveryOf, stopSentence } from '../utils/emailDelivery';
import ApiKeysPanel from '../components/users/ApiKeysPanel';
import ConnectedAssistantsPanel from '../components/users/ConnectedAssistantsPanel';
import AccountProtectionSection from '../components/users/AccountProtectionSection';
import AccountDeleteSummary from '../components/users/AccountDeleteSummary';
import ConfirmWordModal from '../components/common/ConfirmWordModal';
import { UploadOutlined } from '@ant-design/icons';
import { DataState, Skeleton, deriveDataState } from '../components/loading';
import PlanSection from '../components/account/PlanSection';
import DevicesSection from '../components/account/DevicesSection';
import { PLANS_VISIBLE } from '../config/plans';

const { Text } = Typography;

// Accept iPhone HEIC picks too — they're transcoded to JPEG in the browser
// before upload (utils/imageNormalize), so the API's allowlist stays strict.
const UPLOAD_ACCEPT = [...AVATAR_MIMES, 'image/heic', 'image/heif', '.heic', '.heif'].join(',');

/**
 * Avatar identity editor: a photo (uploaded through the files service,
 * processed server-side into cached variants) OR an icon+color mark.
 * Changes apply immediately — the server deletes a replaced photo's file,
 * so there is nothing sensible to "cancel" back to.
 */
function AvatarEditor({ userId }) {
	const { message } = App.useApp();
	const { user, refresh } = useCurrentUser();
	const [saving, setSaving] = useState(false);
	const [icon, setIcon] = useState('');
	const [color, setColor] = useState('#1677ff');

	const applyAvatar = async (avatar, successText) => {
		if (!userId) return false;
		setSaving(true);
		try {
			await api.put(`${API_USER_URL}/${userId}`, { avatar });
			await refresh(); // header + every other consumer updates at once
			message.success(successText);
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not update your avatar.');
			return false;
		} finally {
			setSaving(false);
		}
	};

	// Cheap precheck only (LIST_IGNORE keeps obvious non-images out of antd's
	// internal list); real type/size handling happens in normalizeAvatarImage,
	// which transcodes HEIC and downscales oversized picks instead of
	// rejecting them. Files-app picks can arrive with an empty type — let
	// them through to the decode attempt.
	const precheckUpload = (file) => {
		const looksLikeImage = !file.type
			|| file.type.startsWith('image/')
			|| /\.(heic|heif)$/i.test(file.name || '');
		if (!looksLikeImage) {
			message.error('Avatar must be an image.');
			return Upload.LIST_IGNORE;
		}
		return true;
	};

	const handleUpload = async ({ file, onSuccess, onError }) => {
		setSaving(true);
		let upload;
		try {
			upload = await normalizeAvatarImage(file);
		} catch (err) {
			message.error(err?.code === 'IMAGE_TOO_LARGE'
				? `That image is too large even after resizing — avatars are capped at ${AVATAR_MAX_MB}MB.`
				: 'Couldn\'t read that image — try a JPEG or PNG.');
			setSaving(false);
			onError?.(err);
			return;
		}
		let doc = null;
		try {
			doc = await uploadFile(upload, 'avatar');
		} catch (err) {
			message.error(apiErrorMessage(err, 'Upload failed.'));
			setSaving(false);
			onError?.(err);
			return;
		}
		const ok = await applyAvatar({ type: 'photo', fileId: String(doc._id) }, 'Profile photo updated.');
		if (ok) {
			onSuccess?.({}, file);
		} else {
			// The upload landed but the profile PUT didn't — reap the file so
			// it doesn't sit orphaned under this user's ownership.
			deleteFile(String(doc._id)).catch(() => {});
			onError?.(new Error('Avatar update failed.'));
		}
	};

	const handleUseIcon = () => {
		if (!icon) return;
		// InputColor emits hex; trim a possible alpha channel and only send a
		// color the API's #rrggbb pattern will accept.
		const hex = (color || '').slice(0, 7);
		const validHex = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : null;
		applyAvatar(
			{ type: 'icon', icon, ...(validHex ? { color: validHex } : {}) },
			'Avatar updated.'
		);
	};

	return (
		<div className='mt-4'>
			<h3>Avatar</h3>
			<Space align='center' size='large'>
				<UserAvatar user={user} size={64} />
				<Space direction='vertical' size='small'>
					<Upload
						accept={UPLOAD_ACCEPT}
						showUploadList={false}
						beforeUpload={precheckUpload}
						customRequest={handleUpload}
						disabled={saving || !userId}
					>
						<Button icon={<UploadOutlined />} loading={saving} disabled={!userId}>
							Upload a photo
						</Button>
					</Upload>
					{user?.avatar ? (
						<Button size='small' danger type='text' disabled={saving} onClick={() => applyAvatar(null, 'Avatar removed.')}>
							Remove avatar
						</Button>
					) : null}
				</Space>
			</Space>

			<Divider plain>or pick an icon</Divider>
			<InputIconSelect elementName='avatarIcon' elementLabel='Icon' value={icon} onChange={(_name, value) => setIcon(value)} />
			<InputColor elementName='avatarColor' elementLabel='Color' value={color} onChange={(_name, value) => setColor(value)} />
			<Button onClick={handleUseIcon} disabled={!icon || saving || !userId}>
				Use icon as avatar
			</Button>
		</div>
	);
}

/**
 * Why keepr is not mailing this address, when it is not (plan §3.1–3.5): the
 * bounce banner's fuller story, beside the address it is about. A spam report
 * the person made points at Resume emails on their Notifications page; a stop
 * they cannot lift themselves says who can.
 */
function EmailDeliveryNote({ user }) {
	const delivery = deliveryOf(user);
	if (!delivery) return null;
	if (delivery.state === 'complained') {
		return (
			<Alert
				type='info'
				showIcon
				style={{ marginTop: 12 }}
				data-testid='email-delivery-note'
				message='Your notification emails are off'
				description={<>{complaintSentence(delivery, user.email)}, so keepr turned your notification emails off. If that was a mistake, choose <Link to='/account/notifications'>Resume emails</Link> on your Notifications page.</>}
			/>
		);
	}
	const temporary = delivery.state === 'bounced' && delivery.until;
	return (
		<Alert
			type='warning'
			showIcon
			style={{ marginTop: 12 }}
			data-testid='email-delivery-note'
			message="keepr isn't emailing this address"
			description={temporary
				? stopSentence(delivery)
				: <>{stopSentence(delivery)} Until it is lifted, a password reset can&rsquo;t reach you either. If the address works again, a keepr administrator can lift the stop.</>}
		/>
	);
}

/**
 * The verification data point: the badge, and — while unconfirmed — the
 * resend button, since sharing, uploads, keys and exports wait for it.
 */
function VerificationStatus({ user }) {
	const { message } = App.useApp();
	const [sending, setSending] = useState(false);
	if (!user) return null;

	const resend = async () => {
		setSending(true);
		try {
			await api.post(API_RESEND_VERIFICATION_URL);
			message.success(`Confirmation email sent to ${user.email}. The link is good for seven days.`);
		} catch (error) {
			message.error(apiErrorMessage(error, 'Could not send the confirmation email.'));
		} finally {
			setSending(false);
		}
	};

	return (
		<div className='mt-3' data-testid='verification-status'>
			<Space align='center' wrap>
				<Text type='secondary'>Email:</Text>
				<VerifiedBadge verified={user.emailVerified === true} verifiedAt={user.emailVerifiedAt} />
				{user.emailVerified !== true ? (
					<Button size='small' type='primary' loading={sending} onClick={resend}>Resend confirmation email</Button>
				) : null}
			</Space>
			{user.emailVerified !== true ? (
				<div><Text type='secondary' style={{ fontSize: 12 }}>Sharing, uploads, API keys and exports are available once your address is confirmed.</Text></div>
			) : null}
			<EmailDeliveryNote user={user} />
		</div>
	);
}

/**
 * Sessions and the account itself: sign out everywhere, and deleting the
 * account. The delete dialog shows what goes before it arms — the API's own
 * preview, including the records other people wrote in the collections that
 * go (delete audit F4) — and asks for the account's email address typed out
 * AND the password: the address says "I know which account this is", the
 * password is what the API checks.
 */
export function SecuritySection({ user }) {
	const { message, modal } = App.useApp();
	const navigate = useNavigate();
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [password, setPassword] = useState('');
	const [deleting, setDeleting] = useState(false);
	// The delete preview: null until the dialog opens, then the API's counts.
	const [preview, setPreview] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const admin = isAdmin();
	// ConfirmWordModal compares lowercased; an account with no address on
	// file (none today) falls back to the plain word.
	const confirmWord = String(user?.email || 'delete').trim().toLowerCase();

	const openDelete = () => {
		setDeleteOpen(true);
		setPreview(null);
		setPreviewLoading(true);
		api.get(API_USER_ME_DELETE_PREVIEW_URL)
			.then(({ data }) => setPreview(data || null))
			// The dialog still works without the counts; the API decides.
			.catch(() => setPreview(null))
			.finally(() => setPreviewLoading(false));
	};
	const closeDelete = () => {
		setDeleteOpen(false);
		setPassword('');
	};

	const signOutEverywhere = () => {
		modal.confirm({
			title: 'Sign out everywhere?',
			content: 'Every device signed in to your account, including this one, is signed out. You can sign in again right away.',
			okText: 'Sign out everywhere',
			onOk: async () => {
				try {
					await api.post(API_LOGOUT_ALL_URL);
					clearSessionToken();
					clearLocalDrafts();
					navigate('/login');
				} catch (error) {
					message.error(apiErrorMessage(error, 'Could not sign you out everywhere.'));
				}
			},
		});
	};

	const deleteAccount = async () => {
		setDeleting(true);
		try {
			await api.delete(API_USER_ME_URL, { data: { password } });
			clearSessionToken();
			clearLocalDrafts();
			setDeleteOpen(false);
			navigate('/', { replace: true });
			message.success('Your account and everything it owned have been deleted.');
		} catch (error) {
			message.error(apiErrorMessage(error, 'Could not delete your account.'));
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className='mt-4'>
			<h3>Security</h3>
			<Space wrap>
				<Link to='/account/change-password'><Button>Change password</Button></Link>
				<Button onClick={signOutEverywhere}>Sign out everywhere</Button>
			</Space>
			<p className='text-muted small mt-2'>Changing your password also signs out every other device.</p>

			<Divider plain />
			<h3>Delete account</h3>
			<p className='text-muted small'>
				Deletes your account, your API keys, favorites and preferences at once. Your collections — with their items, files and
				share links — are deleted with it and erased for good after 30 days. Records you added to other people&apos;s collections
				stay with those collections.
			</p>
			{admin ? (
				<Text type='secondary'>Admin accounts cannot be self-deleted. Remove the admin role first.</Text>
			) : (
				<Button danger onClick={openDelete}>Delete my account…</Button>
			)}

			<ConfirmWordModal
				title='Delete your account?'
				open={deleteOpen}
				word={confirmWord}
				okText='Delete my account'
				onOk={deleteAccount}
				onCancel={closeDelete}
				extraValid={Boolean(password)}
				busy={deleting}
			>
				<p>You are signed out and cannot sign in again.</p>
				<AccountDeleteSummary
					owner='You'
					counts={preview?.counts}
					collections={preview?.collections}
					purgeAfterDays={preview?.purgeAfterDays}
					loading={previewLoading}
				/>
				<Input.Password
					className='mb-3'
					value={password}
					onChange={e => setPassword(e.target.value)}
					placeholder='Your password'
					autoComplete='current-password'
					aria-label='Password'
				/>
			</ConfirmWordModal>
		</div>
	);
}

export default function Profile() {

	// The heading wears the same identity mark the rest of the app shows for
	// this person. No chip: on your own account settings there is nothing a
	// "who is this?" modal could tell you.
	const { user: currentUser, refresh: refreshUser } = useCurrentUser();
	const { message } = App.useApp();

	const [data, setData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		dob: '',
		// userId: ''
	});

	// The profile read: until it answers the form is a skeleton, never blank
	// inputs with a live Save; a failure is an error with Retry, not a blank form.
	const [ profileLoaded, setProfileLoaded ] = useState( false );
	const [ profileFailed, setProfileFailed ] = useState( false );
	const [ profileAttempt, setProfileAttempt ] = useState( 0 );

	const handleChange = ( name, value ) => setData(prevState => ({ ...prevState, [ name ]: value }));

	const handleSubmit = () => {

		api.put( API_USER_URL + '/' + data.userId, data ).then( res => {
			if ( [ 200, 201 ].indexOf( res.status ) !== -1 ) {
				message.success( 'Profile saved.' );
			}
		} ).catch( error => message.error( apiErrorMessage( error, 'Could not save your profile.' ) ) );

	};



	useEffect(() => {

		setProfileFailed( false );
		api.get( API_PROFILE_URL ).then( data => {
			if ( data.status !== 200 ) {
				setProfileFailed( true );
				return null;
			}

			setData( {
				firstName: data.data.firstName,
				lastName: data.data.lastName,
				// No `username` (KPR-547): it is not a profile field — only a
				// test or demo account has one, and the API drops it from a save.
				email: data.data.email,
				dob: data.data.dob,
				userId: data.data._id,
			} );
			setProfileLoaded( true );
		} ).catch( () => {
			// 401 is handled globally by the api client; anything else is an error with Retry.
			setProfileFailed( true );
		} );
	}, [ profileAttempt ] );



	return (<>
		<h1 className=''>Account Settings</h1>

		<Space align='center' size='middle'>
			<UserAvatar user={currentUser} size={40} />
			{profileLoaded ? <h2 className='mb-0'>{data.firstName} {data.lastName}</h2> : <Skeleton variant='block' width={180} height={32} />}
		</Space>

		<VerificationStatus user={currentUser} />

		<DataState
			state={deriveDataState( { loading: ! profileLoaded && ! profileFailed, error: profileFailed && ! profileLoaded ? true : null, hasData: profileLoaded } )}
			hasData={profileLoaded}
			skeleton={<Skeleton variant='form-field' count={3} />}
			onRetry={() => setProfileAttempt( n => n + 1 )}
			label='your profile'
		>
		<Form className='m-1'>
			{/* Explicit tokens: InputText defaults autofill OFF, and these are
			    the account holder's own details — the one place it belongs. */}
			<InputText elementName={'email'} elementLabel={'Email'} value={data.email} onChange={handleChange} disabled={true} autoComplete='email' />
			<InputText elementName={'firstName'} elementLabel={'First name'} value={data.firstName} onChange={handleChange} autoComplete='given-name' />
			<InputText elementName={'lastName'} elementLabel={'Last name'} value={data.lastName} onChange={handleChange} autoComplete='family-name' />
			{/* <InputDate elementName={'dob'} elementLabel={'Date of birth'} value={data.dob} onChange={handleChange} /> */}

		<Button onClick={handleSubmit} >
			Save
		</Button>
	</Form>
		</DataState>

	<AvatarEditor userId={data.userId} />

	{/* Account › Plan (KPR-1095): each meter as "used of limit". Dark until
	    billing ships — not mounted, and nothing asked, while the flag is off
	    (config/plans.js). */}
	{PLANS_VISIBLE ? <PlanSection /> : null}

	{/* Account › Tags (docs/plans/tags.md §5.2): the person's own tags,
	    managed on a page of their own. */}
	<div className='mt-4 account-tags-intro'>
		<h3>Private tags</h3>
		<p className='text-muted small'>Tags only you see: nobody else, not even a collection&rsquo;s managers.</p>
		<Link to='/account/tags'><Button>Manage private tags</Button></Link>
	</div>

	{/* The door to the assistant path sits above the keys it makes: a
	    person who has never held a key should not have to read the panel
	    to find out what one is for. */}
	<div className='mt-4 form-panel connect-intro'>
		<h3>Connect an assistant</h3>
		<p className='text-muted small'>Let Claude, ChatGPT or a coding agent read, add and change records for you.</p>
		<Link to='/account/connect'><Button type='primary'>Connect an assistant</Button></Link>
	</div>

	<div className='mt-4'>
		<ApiKeysPanel />
	</div>

	{/* Assistants that connected without a key (the OAuth path), and
	    the way to disconnect one. */}
	<div className='mt-4'>
		<ConnectedAssistantsPanel />
	</div>

	{/* Two-step sign-in and the signing PIN: is this account really them,
	    and who performed this one act on a shared device. */}
	<AccountProtectionSection user={currentUser} onChanged={refreshUser} />

	{/* The phones signed in through the app (KPR-1674), each signed out
	    alone; on every platform, so a lost phone is signed out from here. */}
	<DevicesSection />

	<SecuritySection user={currentUser} />



		<AdminJSON obj={data} label='user data' />

	</>);

};
